<script>
/**
 * @component UrgencyQueue
 * @description Cola de pacientes de urgencias con cards draggable y tabs
 *   En espera / Historial. Doble modo: médico (ve su especialidad asignada,
 *   puede tomar pacientes) y admin (ve todas las especialidades, puede
 *   reordenar con drag & drop, subir/bajar posición y cancelar).
 *   Incluye polling cada 15s y timer en vivo para el tiempo de espera.
 * @example <UrgencyQueue />
 */
import urgencyQueueService from "@/services/urgency-queue";
import urgencyProviderService from "@/services/urgency-provider";
import urgencyConfigService from "@/services/urgency-config";
import Spinner from "@/components/ui/Spinner";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import draggable from "vuedraggable";
import { mapGetters } from "vuex";

/** @description Opciones de filtro de fecha para el historial */
const DATE_FILTERS = [
  { value: "today", label: "Hoy" },
  { value: "7days", label: "Últimos 7 días" },
  { value: "30days", label: "Último mes" },
];

export default {
  name: "UrgencyQueue",
  components: { Spinner, BaseSubNav, draggable },

  data() {
    return {
      isLoading: true,
      isAssigned: false,
      assignment: null,
      urgencyConfigs: [],
      activeSpecialtyId: "",
      queue: [],
      now: Date.now(),
      pollInterval: null,
      tickInterval: null,
      takingId: null,
      cancellingId: null,
      // Tabs
      activeTab: "waiting",
      // History
      history: [],
      isLoadingHistory: false,
      historyDateFilter: "today",
      historySearch: "",
      dateFilters: DATE_FILTERS,
    };
  },

  computed: {
    ...mapGetters(["isAdmin", "isHealthAdmin", "isDoctor"]),

    /**
     * @description Si el usuario tiene rol administrativo
     * @returns {boolean}
     */
    isAdminLike() {
      return this.isAdmin || this.isHealthAdmin;
    },

    /**
     * @description Nombre de la especialidad activa resuelto desde configs o assignment
     * @returns {string}
     */
    activeSpecialtyName() {
      if (!this.activeSpecialtyId) return "";
      if (this.isAdminLike) {
        const config = this.urgencyConfigs.find(
          (c) => c.medicalSpecialty?._id === this.activeSpecialtyId
        );
        return config?.medicalSpecialty?.name || "";
      }
      if (this.assignment?.specialties?.length) {
        const spec = this.assignment.specialties.find(
          (s) => s.medicalSpecialty?._id === this.activeSpecialtyId
        );
        return spec?.medicalSpecialty?.name || "";
      }
      return "";
    },

    /**
     * @description Cantidad de pacientes en espera
     * @returns {number}
     */
    waitingCount() {
      return this.queue.filter(
        (q) => q.status === "waiting" || q.status === "pending"
      ).length;
    },

    /**
     * @description Cantidad de items en el historial
     * @returns {number}
     */
    historyCount() {
      return this.history.length;
    },

    /**
     * @description Tiempo promedio de espera en minutos
     * @returns {number}
     */
    avgWaitMinutes() {
      const waiting = this.queue.filter(
        (q) => q.status === "waiting" || q.status === "pending"
      );
      if (!waiting.length) return 0;
      const total = waiting.reduce((sum, q) => {
        if (!q.requestedAt) return sum;
        return sum + Math.max(0, this.now - new Date(q.requestedAt).getTime());
      }, 0);
      return Math.round(total / waiting.length / 60000);
    },

    /**
     * @description Cola ordenada por prioridad ascendente (mutable para drag)
     * @returns {Array}
     */
    sortedQueue: {
      get() {
        return [...this.queue].sort(
          (a, b) => (a.priority || 999) - (b.priority || 999)
        );
      },
      set(val) {
        this.queue = val;
      },
    },

    /**
     * @description Historial filtrado por búsqueda local (complementa filtro server-side)
     * @returns {Array}
     */
    filteredHistory() {
      if (!this.historySearch.trim()) return this.history;
      const q = this.historySearch.toLowerCase().trim();
      return this.history.filter((item) => {
        const name = this.getPatientName(item).toLowerCase();
        const dni = this.getPatientDni(item).toLowerCase();
        return name.includes(q) || dni.includes(q);
      });
    },

    /**
     * @description Especialidades disponibles para tabs (médico) o dropdown (admin)
     * @returns {Array<{_id: string, name: string}>}
     */
    availableSpecialties() {
      if (this.isAdminLike) {
        return this.urgencyConfigs
          .filter((c) => c.isEnabled !== false && c.medicalSpecialty)
          .map((c) => ({
            _id: c.medicalSpecialty._id,
            name: c.medicalSpecialty.name,
          }));
      }
      if (this.assignment && this.assignment.specialties) {
        return this.assignment.specialties
          .filter((s) => s.medicalSpecialty)
          .map((s) => ({
            _id: s.medicalSpecialty._id,
            name: s.medicalSpecialty.name,
          }));
      }
      return [];
    },
  },

  async created() {
    await this.init();
    this.startPolling();
    this.startTick();
  },

  beforeDestroy() {
    this.stopPolling();
    this.stopTick();
  },

  methods: {
    /**
     * @description Inicializa la vista según el rol del usuario
     * @returns {Promise<void>}
     */
    async init() {
      this.isLoading = true;
      try {
        if (this.isAdminLike) {
          const raw = await urgencyConfigService.getAll();
          const configs = raw.docs || raw;
          this.urgencyConfigs = configs.filter(
            (c) => c.isEnabled !== false && c.medicalSpecialty
          );
          this.isAssigned = true;
          if (this.urgencyConfigs.length) {
            this.activeSpecialtyId =
              this.urgencyConfigs[0].medicalSpecialty._id;
            await this.fetchQueue();
          }
        } else if (this.isDoctor) {
          const res = await urgencyProviderService.getMyAssignment();
          if (res && (res.isAssigned || res.specialties?.length)) {
            this.isAssigned = true;
            this.assignment = res;
            const specs = res.specialties || [];
            if (specs.length) {
              this.activeSpecialtyId = specs[0].medicalSpecialty._id;
              await this.fetchQueue();
            }
          } else {
            this.isAssigned = false;
          }
        }
      } catch (e) {
        this.$message.error("Error al cargar datos de urgencias");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Carga la cola para la especialidad activa.
     * @returns {Promise<void>}
     */
    async fetchQueue() {
      if (!this.activeSpecialtyId) return;
      try {
        this.queue = await urgencyQueueService.getBySpecialty(
          this.activeSpecialtyId
        );
      } catch (e) {
        console.error("Error fetching urgency queue", e);
      }
    },

    /**
     * @description Carga el historial para la especialidad activa con filtros
     * @returns {Promise<void>}
     */
    async fetchHistory() {
      if (!this.activeSpecialtyId) return;
      this.isLoadingHistory = true;
      try {
        const { dateFrom, dateTo } = this.getDateRange(this.historyDateFilter);
        const HISTORY_STATUSES = ["completed", "cancelled", "in-progress"];
        const raw = await urgencyQueueService.getHistory({
          specialtyId: this.activeSpecialtyId,
          status: HISTORY_STATUSES.join(","),
          dateFrom,
          dateTo,
          search: this.historySearch.trim() || undefined,
        });
        // Filtro client-side por si el backend no filtra por status
        const items = Array.isArray(raw) ? raw : raw.docs || [];
        this.history = items.filter((i) => HISTORY_STATUSES.includes(i.status));
      } catch (e) {
        console.error("Error fetching history", e);
      } finally {
        this.isLoadingHistory = false;
      }
    },

    /**
     * @description Calcula rango de fechas ISO desde el filtro seleccionado
     * @param {string} filter - Valor del filtro (today, 7days, 30days)
     * @returns {{ dateFrom: string, dateTo: string }}
     */
    getDateRange(filter) {
      const now = new Date();
      const dateTo = now.toISOString();
      const from = new Date(now);
      if (filter === "today") {
        from.setHours(0, 0, 0, 0);
      } else if (filter === "7days") {
        from.setDate(from.getDate() - 7);
      } else if (filter === "30days") {
        from.setDate(from.getDate() - 30);
      }
      return { dateFrom: from.toISOString(), dateTo };
    },

    /**
     * @description Cambia el tab activo y carga datos correspondientes
     * @param {string} tab - 'waiting' o 'history'
     */
    async setTab(tab) {
      this.activeTab = tab;
      if (tab === "history" && !this.history.length) {
        await this.fetchHistory();
      }
    },

    /**
     * @description Handler para cambio de filtro de fecha en historial
     */
    async onHistoryFilterChange() {
      await this.fetchHistory();
    },

    /**
     * @description Cambia la especialidad activa y recarga la cola
     * @param {string} specId - ID de la especialidad
     */
    async switchSpecialty(specId) {
      this.activeSpecialtyId = specId;
      this.history = [];
      await this.fetchQueue();
      if (this.activeTab === "history") {
        await this.fetchHistory();
      }
    },

    /** @description Inicia el polling de la cola cada 15 segundos */
    startPolling() {
      this.pollInterval = setInterval(() => {
        this.fetchQueue();
      }, 15000);
    },

    /** @description Detiene el polling */
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    /** @description Inicia el tick del timer cada segundo */
    startTick() {
      this.tickInterval = setInterval(() => {
        this.now = Date.now();
      }, 1000);
    },

    /** @description Detiene el tick del timer */
    stopTick() {
      if (this.tickInterval) {
        clearInterval(this.tickInterval);
        this.tickInterval = null;
      }
    },

    /**
     * @description Calcula el tiempo de espera formateado como "Xm Ys"
     * @param {string} requestedAt - Fecha ISO del pedido
     * @returns {string}
     */
    waitTime(requestedAt) {
      if (!requestedAt) return "—";
      const diff = Math.max(0, this.now - new Date(requestedAt).getTime());
      const totalSecs = Math.floor(diff / 1000);
      const mins = Math.floor(totalSecs / 60);
      const secs = totalSecs % 60;
      return `${mins}m ${secs < 10 ? "0" : ""}${secs}s`;
    },

    /**
     * @description Formatea una fecha ISO a fecha/hora legible
     * @param {string} date - Fecha ISO
     * @returns {string}
     */
    formatDateTime(date) {
      if (!date) return "—";
      return new Date(date).toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    /**
     * @description Obtiene el nombre completo del paciente
     * @param {Object} item - Item de la cola con datos del paciente
     * @returns {string}
     */
    getPatientName(item) {
      if (item.patient) {
        const p = item.patient;
        return `${p.firstName || ""} ${p.lastName || ""}`.trim() || "—";
      }
      return "—";
    },

    /**
     * @description Obtiene las iniciales del paciente para el avatar
     * @param {Object} item - Item de la cola
     * @returns {string}
     */
    getPatientInitials(item) {
      if (!item.patient) return "?";
      const f = (item.patient.firstName || "")[0] || "";
      const l = (item.patient.lastName || "")[0] || "";
      return (f + l).toUpperCase() || "?";
    },

    /**
     * @description Obtiene el DNI del paciente (governmentId.number del populate)
     * @param {Object} item - Item de la cola con datos del paciente
     * @returns {string}
     */
    getPatientDni(item) {
      if (item.patient?.governmentId?.number)
        return item.patient.governmentId.number;
      return "—";
    },

    /**
     * @description Obtiene el nombre del médico que atendió
     * @param {Object} item - Item de la cola
     * @returns {string}
     */
    getAttendingDoctor(item) {
      const d = item.attendedBy || item.doctor;
      if (!d) return "—";
      return `Dr/a. ${d.lastName || ""}, ${d.firstName || ""}`.trim();
    },

    /**
     * @description Devuelve label y clase del badge de status para historial
     * @param {string} status - Status del item
     * @returns {{ label: string, cssClass: string }}
     */
    getStatusBadge(status) {
      const map = {
        completed: { label: "Atendido", cssClass: "status-badge--completed" },
        "in-progress": {
          label: "En atención",
          cssClass: "status-badge--progress",
        },
        cancelled: { label: "Cancelado", cssClass: "status-badge--cancelled" },
      };
      return map[status] || { label: status || "—", cssClass: "" };
    },

    /**
     * @description Verifica si el item es el que más tiempo lleva esperando
     * @param {Object} item - Item de la cola
     * @param {number} index - Índice en la cola ordenada
     * @returns {boolean}
     */
    isLongestWaiting(item, index) {
      return (
        index === 0 && (item.status === "waiting" || item.status === "pending")
      );
    },

    /**
     * @description Devuelve la clase CSS del borde izquierdo según prioridad
     * @param {Object} item - Item de la cola
     * @returns {string}
     */
    getPriorityClass(item) {
      const p = item.priority || 999;
      if (p <= 2) return "queue-card--high";
      if (p <= 5) return "queue-card--medium";
      return "queue-card--low";
    },

    /**
     * @description Toma un paciente de la cola para atenderlo
     * @param {Object} item - Item de la cola
     * @returns {Promise<void>}
     */
    async takePatient(item) {
      this.takingId = item._id;
      try {
        const res = await urgencyQueueService.take(item._id);
        this.$message.success("Paciente tomado, redirigiendo...");
        const appointmentId = res.appointment?._id || res.appointment;
        this.$router.push({
          name: "appointment",
          params: { appointmentId },
        });
      } catch (e) {
        const status = e?.response?.status;
        if (status === 409) {
          this.$message.warning("Este paciente ya fue tomado por otro médico");
          this.fetchQueue();
        } else {
          this.$message.error("Error al tomar paciente");
        }
      } finally {
        this.takingId = null;
      }
    },

    /**
     * @description Cancela un item de la cola (admin). Pide confirmación antes.
     * @param {Object} item - Item de la cola
     * @returns {Promise<void>}
     */
    async cancelItem(item) {
      try {
        await this.$confirm(
          `¿Cancelar la atención de ${this.getPatientName(item)}?`,
          "Confirmar cancelación",
          {
            confirmButtonText: "Cancelar turno",
            cancelButtonText: "Volver",
            type: "warning",
          }
        );
      } catch {
        return;
      }
      this.cancellingId = item._id;
      try {
        await urgencyQueueService.cancel(item._id);
        this.$message.success("Turno cancelado");
        await this.fetchQueue();
      } catch (e) {
        // BUG-EFQS85: antes mostraba siempre "Error al cancelar turno" sin
        // dar pistas. Ahora extraemos el mensaje del backend (que distingue
        // 404 / 409 estado terminal / 403 permisos).
        const backendMsg =
          (e && e.response && e.response.data && e.response.data.error) ||
          (e && e.message) ||
          "Error desconocido";
        this.$message.error(`No se pudo cancelar: ${backendMsg}`);
      } finally {
        this.cancellingId = null;
      }
    },

    /**
     * @description Sube un item una posición en la cola
     * @param {number} index - Índice en sortedQueue
     * @returns {Promise<void>}
     */
    async moveUp(index) {
      if (index <= 0) return;
      const sorted = this.sortedQueue;
      const current = sorted[index];
      const above = sorted[index - 1];
      const tempPriority = current.priority;
      try {
        await Promise.all([
          urgencyQueueService.updatePriority(current._id, above.priority),
          urgencyQueueService.updatePriority(above._id, tempPriority),
        ]);
        await this.fetchQueue();
      } catch (e) {
        this.$message.error("Error al cambiar posición");
      }
    },

    /**
     * @description Baja un item una posición en la cola
     * @param {number} index - Índice en sortedQueue
     * @returns {Promise<void>}
     */
    async moveDown(index) {
      const sorted = this.sortedQueue;
      if (index >= sorted.length - 1) return;
      const current = sorted[index];
      const below = sorted[index + 1];
      const tempPriority = current.priority;
      try {
        await Promise.all([
          urgencyQueueService.updatePriority(current._id, below.priority),
          urgencyQueueService.updatePriority(below._id, tempPriority),
        ]);
        await this.fetchQueue();
      } catch (e) {
        this.$message.error("Error al cambiar posición");
      }
    },

    /**
     * @description Handler de fin de drag. Recalcula prioridades y batch update.
     * @returns {Promise<void>}
     */
    async onDragEnd() {
      const updates = this.queue
        .map((item, idx) => {
          const newPriority = idx + 1;
          if (item.priority !== newPriority) {
            return urgencyQueueService.updatePriority(item._id, newPriority);
          }
          return null;
        })
        .filter(Boolean);

      if (!updates.length) return;
      try {
        await Promise.all(updates);
        await this.fetchQueue();
      } catch (e) {
        this.$message.error("Error al reordenar cola");
        await this.fetchQueue();
      }
    },
  },
};
</script>

<template lang="pug">
section.urgency-queue
  BaseSubNav(:showNavigation="isAssigned && !isLoading")
    template(#navigation)
      button.page-toolbar__tab(
        :class="{ 'page-toolbar__tab--active': activeTab === 'waiting' }"
        @click="setTab('waiting')"
      )
        | En espera
        span.toolbar-tabs__count(v-if="waitingCount > 0") {{ waitingCount }}
      button.page-toolbar__tab(
        :class="{ 'page-toolbar__tab--active': activeTab === 'history' }"
        @click="setTab('history')"
      )
        | Historial
        span.toolbar-tabs__count(v-if="historyCount > 0") {{ historyCount }}
    template(#actions)
      .urgency-queue__header-actions
        el-select.urgency-queue__specialty-select(
          v-if="availableSpecialties.length > 1"
          v-model="activeSpecialtyId"
          placeholder="Especialidad"
          size="small"
          @change="switchSpecialty"
        )
          el-option(
            v-for="spec in availableSpecialties"
            :key="spec._id"
            :label="spec.name"
            :value="spec._id"
          )
        .urgency-queue__badge(v-if="waitingCount > 0")
          span {{ waitingCount }}
          | &nbsp;en espera

  //- Loading
  .urgency-queue__loading(v-if="isLoading")
    spinner

  //- No asignado (médico)
  .urgency-queue__not-assigned(v-else-if="!isAssigned")
    micon(name="alert")
    p No estás asignado para atender urgencias
    p.urgency-queue__not-assigned-sub Contactá al administrador para ser asignado a una especialidad de urgencia.

  //- Vista principal
  template(v-else)
    //- ═══════ TAB: En espera ═══════
    template(v-if="activeTab === 'waiting'")
      //- Stats bar
      .queue-stats(v-if="sortedQueue.length")
        .queue-stats__item
          micon(name="people")
          span {{ waitingCount }} en espera
        .queue-stats__item
          micon(name="schedule")
          span Tiempo promedio: {{ avgWaitMinutes }}m
        .queue-stats__item(v-if="activeSpecialtyName")
          micon(name="local_hospital")
          span {{ activeSpecialtyName }}

      //- Cola como cards
      .urgency-queue__body
        template(v-if="sortedQueue.length")
          //- Admin: draggable
          draggable(
            v-if="isAdminLike"
            v-model="sortedQueue"
            handle=".drag-handle"
            @end="onDragEnd"
            tag="div"
            class="queue-list"
          )
            .queue-card(
              v-for="(item, idx) in sortedQueue"
              :key="item._id"
              :class="[getPriorityClass(item), { 'queue-card--longest': isLongestWaiting(item, idx) }]"
            )
              .drag-handle
                micon(name="drag_indicator")
              .queue-card__pos {{ idx + 1 }}
              .queue-card__avatar
                span {{ getPatientInitials(item) }}
              .queue-card__info
                .queue-card__name {{ getPatientName(item) }}
                .queue-card__dni DNI: {{ getPatientDni(item) }}
              .queue-card__wait
                micon(name="schedule")
                span {{ waitTime(item.requestedAt) }}
              .queue-card__reason {{ item.reason || '—' }}
              .queue-card__actions
                button.queue-btn.queue-btn--up(@click="moveUp(idx)" :disabled="idx === 0" title="Subir")
                  micon(name="expand_less")
                button.queue-btn.queue-btn--down(@click="moveDown(idx)" :disabled="idx === sortedQueue.length - 1" title="Bajar")
                  micon(name="expand_more")
                button.queue-btn-cancel(@click="cancelItem(item)" :disabled="cancellingId === item._id")
                  spinner(v-if="cancellingId === item._id" size="8")
                  template(v-else)
                    micon(name="close")
                    span Cancelar

          //- Médico: lista sin drag
          .queue-list(v-else)
            .queue-card(
              v-for="(item, idx) in sortedQueue"
              :key="item._id"
              :class="[getPriorityClass(item), { 'queue-card--longest': isLongestWaiting(item, idx) }]"
            )
              .queue-card__pos {{ idx + 1 }}
              .queue-card__avatar
                span {{ getPatientInitials(item) }}
              .queue-card__info
                .queue-card__name {{ getPatientName(item) }}
                .queue-card__dni DNI: {{ getPatientDni(item) }}
              .queue-card__wait
                micon(name="schedule")
                span {{ waitTime(item.requestedAt) }}
              .queue-card__reason {{ item.reason || '—' }}
              .queue-card__actions
                button.button-teal-new.button--take(
                  v-if="item.status === 'waiting' || item.status === 'pending'"
                  @click="takePatient(item)"
                  :disabled="takingId === item._id"
                )
                  spinner(v-if="takingId === item._id" size="8")
                  span(v-else) Atender

        .urgency-queue__empty(v-else)
          micon(name="check_circle_outline")
          p No hay pacientes en espera

    //- ═══════ TAB: Historial ═══════
    template(v-if="activeTab === 'history'")
      //- Filtros
      .history-filters
        el-select.history-filters__date(
          v-model="historyDateFilter"
          size="small"
          @change="onHistoryFilterChange"
        )
          el-option(
            v-for="f in dateFilters"
            :key="f.value"
            :label="f.label"
            :value="f.value"
          )
        el-input.history-filters__search(
          v-model="historySearch"
          size="small"
          placeholder="Buscar por nombre o DNI..."
          prefix-icon="el-icon-search"
          clearable
          @clear="fetchHistory"
          @keyup.enter.native="fetchHistory"
        )

      //- Loading historial
      .urgency-queue__loading(v-if="isLoadingHistory")
        spinner

      //- Cards historial
      template(v-else)
        .queue-list(v-if="filteredHistory.length")
          .history-card(
            v-for="item in filteredHistory"
            :key="item._id"
          )
            .history-card__avatar
              span {{ getPatientInitials(item) }}
            .history-card__info
              .history-card__name {{ getPatientName(item) }}
              .history-card__dni DNI: {{ getPatientDni(item) }}
            .history-card__reason {{ item.reason || '—' }}
            .history-card__dates
              .history-card__date
                span.history-card__date-label Ingreso:
                span {{ formatDateTime(item.requestedAt) }}
              .history-card__date
                span.history-card__date-label Atención:
                span {{ formatDateTime(item.attendedAt || item.completedAt) }}
            .history-card__doctor {{ getAttendingDoctor(item) }}
            .history-card__status
              span.status-badge(:class="getStatusBadge(item.status).cssClass") {{ getStatusBadge(item.status).label }}

        .urgency-queue__empty(v-else)
          micon(name="history")
          p No hay registros en el período seleccionado
</template>

<style lang="scss" scoped>
.urgency-queue {
  &__header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__specialty-select {
    width: 180px;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #ef4444;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 12px;
    line-height: 1.4;
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 60px 0;
  }

  &__not-assigned {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 16px;
    text-align: center;

    .micon {
      width: 48px;
      height: 48px;
      fill: $text-muted-new;
      margin-bottom: 16px;
    }

    p {
      font-size: 16px;
      font-weight: 600;
      color: $text-primary-new;
      margin: 0;
    }

    &-sub {
      font-size: 14px !important;
      font-weight: 400 !important;
      color: $text-secondary-new !important;
      margin-top: 6px !important;
    }
  }

  &__controls {
    margin-bottom: 16px;

    .el-select {
      width: 280px;
    }
  }

  &__spec-tabs {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }

  &__spec-tab {
    padding: 8px 16px;
    border: 1px solid $border-color;
    border-radius: 8px;
    background: #fff;
    font-size: 13px;
    font-weight: 500;
    color: $text-secondary-new;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      border-color: #0d9d9d;
      color: #0d9d9d;
    }

    &.is-active {
      background: rgba(13, 157, 157, 0.08);
      border-color: #0d9d9d;
      color: #0d9d9d;
      font-weight: 600;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 16px;
    color: $text-muted-new;

    .micon {
      width: 40px;
      height: 40px;
      fill: #0d9d9d;
      margin-bottom: 12px;
    }

    p {
      font-size: 14px;
      margin: 0;
      color: $text-secondary-new;
    }
  }
}

// ─── Tabs inside BaseSubNav (replicar estilos porque scoped no pasa al slot) ───
.page-toolbar__tab {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: $text-muted-new;
  cursor: pointer;
  border: none;
  border-bottom: 2px solid transparent;
  background: none;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: $navy;
  }

  &--active {
    color: $navy;
    font-weight: 600;
    border-bottom-color: $navy;
  }
}

.toolbar-tabs__count {
  font-size: 10px;
  font-weight: 700;
  background: $navy-light;
  color: $navy;
  padding: 1px 6px;
  border-radius: 10px;
  line-height: 1.4;
}

// ─── Stats bar ───
.queue-stats {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 12px 20px;
  background: #f8fafc;
  border: 1px solid $border-color;
  border-radius: 10px;
  margin-bottom: 16px;

  &__item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: $text-secondary-new;

    .micon {
      width: 16px;
      height: 16px;
      fill: $text-muted-new;
    }
  }
}

// ─── Queue list ───
.queue-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

// ─── Queue card (en espera) ───
.queue-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid $border-color;
  border-left: 4px solid $border-color;
  border-radius: 10px;
  padding: 14px 18px;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &--high {
    border-left-color: #ef4444;
  }

  &--medium {
    border-left-color: #f59e0b;
  }

  &--low {
    border-left-color: #22c55e;
  }

  &--longest {
    background: rgba(13, 157, 157, 0.04);
    border-color: rgba(13, 157, 157, 0.3);
    border-left-width: 4px;

    &.queue-card--high {
      border-left-color: #ef4444;
    }
    &.queue-card--medium {
      border-left-color: #f59e0b;
    }
    &.queue-card--low {
      border-left-color: #22c55e;
    }
  }

  &__pos {
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    background: $navy-light;
    color: $navy;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__avatar {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 50%;
    background: $navy;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__dni {
    font-size: 12px;
    color: $text-secondary-new;
    margin-top: 1px;
  }

  &__wait {
    display: flex;
    align-items: center;
    gap: 4px;
    font-variant-numeric: tabular-nums;
    font-family: "SF Mono", "Menlo", monospace;
    font-size: 13px;
    font-weight: 600;
    color: $navy;
    white-space: nowrap;
    flex-shrink: 0;

    .micon {
      width: 14px;
      height: 14px;
      fill: $text-muted-new;
    }
  }

  &__reason {
    font-size: 13px;
    color: $text-secondary-new;
    flex: 0 1 200px;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }
}

// ─── Drag handle ───
.drag-handle {
  cursor: grab;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.15s;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    background: $navy-light;
  }

  .micon {
    width: 18px;
    height: 18px;
    fill: $text-muted-new;
  }
}

// ─── Queue action buttons ───
.queue-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid $border-color;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;

  .micon {
    width: 16px;
    height: 16px;
    fill: $text-secondary-new;
  }

  &:hover:not(:disabled) {
    border-color: $navy;
    .micon {
      fill: $navy;
    }
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.queue-btn-cancel {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #fecaca;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #ef4444;
  line-height: 30px;
  white-space: nowrap;
  transition: all 0.15s ease;
  vertical-align: middle;

  .micon {
    width: 13px;
    height: 13px;
    fill: #ef4444;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  &:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #ef4444;
    color: #dc2626;
    .micon {
      fill: #dc2626;
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.button--take {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// ─── History filters ───
.history-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  &__date {
    width: 180px;
  }

  &__search {
    flex: 1;
    max-width: 320px;
  }
}

// ─── History card ───
.history-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 14px 18px;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }

  &__avatar {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 50%;
    background: $navy-light;
    color: $navy;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__dni {
    font-size: 12px;
    color: $text-secondary-new;
    margin-top: 1px;
  }

  &__reason {
    font-size: 13px;
    color: $text-secondary-new;
    flex: 0 1 160px;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__dates {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex-shrink: 0;
  }

  &__date {
    font-size: 12px;
    color: $text-secondary-new;
    white-space: nowrap;

    &-label {
      font-weight: 600;
      color: $text-muted-new;
      margin-right: 4px;
    }
  }

  &__doctor {
    font-size: 12px;
    color: $text-secondary-new;
    flex: 0 1 160px;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__status {
    flex-shrink: 0;
  }
}

// ─── Status badges ───
.status-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  white-space: nowrap;

  &--completed {
    background: #dcfce7;
    color: #16a34a;
  }

  &--progress {
    background: #dbeafe;
    color: #1d4ed8;
  }

  &--cancelled {
    background: #f1f5f9;
    color: $text-muted-new;
  }
}

// ─── Responsive ───
@media (max-width: 768px) {
  .queue-stats {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .queue-card {
    flex-wrap: wrap;
    gap: 10px;

    &__reason {
      flex: 1 1 100%;
    }
  }

  .history-filters {
    flex-direction: column;
    align-items: stretch;

    &__search {
      max-width: none;
    }
  }

  .history-card {
    flex-wrap: wrap;
    gap: 10px;

    &__reason,
    &__doctor {
      flex: 1 1 100%;
    }
  }
}
</style>
